import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Skeleton,
  Alert,
  Button,
  Avatar,
  
} from "@mui/material";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ClassRoundedIcon from "@mui/icons-material/ClassRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { useQuery } from "../../hooks/useQuery";
import { fetchClassAnalysis } from "../../utils/analysisApi";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.68)",
  border: "1px solid rgba(255,255,255,0.88)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.09), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const AmbientBackground = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      overflow: "hidden",
      background:
        "linear-gradient(160deg, #f0f4ff 0%, #fafbff 50%, #f5f0ff 100%)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "-100px",
        left: "-80px",
        width: 400,
        height: 400,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: "10%",
        right: "-60px",
        width: 360,
        height: 360,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "radial-gradient(circle, rgba(99,102,241,0.06) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
        maskImage:
          "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
      }}
    />
  </Box>
);

const GRADE_COLORS = {
  A: "#10b981",
  "A+": "#059669",
  "A-": "#34d399",
  B: "#6366f1",
  "B+": "#4f46e5",
  "B-": "#818cf8",
  C: "#f59e0b",
  "C+": "#d97706",
  "C-": "#fbbf24",
  D: "#ef4444",
  "D+": "#dc2626",
  "D-": "#f87171",
  F: "#991b1b",
};
const getGradeColor = (grade) => GRADE_COLORS[grade] || "#8b5cf6";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.[0]) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#a5b4fc",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
            letterSpacing: "0.06em",
          }}
        >
          Grade {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {payload[0].value} students
        </Typography>
      </Box>
    );
  }
  return null;
};

const StatBadge = ({ icon, label, value, color, gradient, index }) => (
  <MotionBox
    initial={{ opacity: 0, scale: 0.88 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.12 + index * 0.1 }}
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
    sx={{
      flex: "1 1 160px",
      p: 2.5,
      borderRadius: "20px",
      ...glassMixin,
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: gradient,
      }}
    />
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        mx: "auto",
        mb: 1.5,
        boxShadow: `0 6px 16px ${color}40`,
        "& svg": { fontSize: 22 },
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: "'Syne', sans-serif",
        fontSize: "2rem",
        fontWeight: 800,
        color: "#111827",
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.7rem",
        color: "#9ca3af",
        fontWeight: 700,
        mt: 0.5,
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
  </MotionBox>
);

const PageSkeleton = () => (
  <Box sx={{ position: "relative", minHeight: "100vh" }}>
    <AmbientBackground />
    <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
      <Skeleton width={200} height={36} sx={{ mb: 3 }} />
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{ flex: "1 1 140px", height: 110, borderRadius: "20px" }}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width="100%"
        height={310}
        sx={{ borderRadius: "24px", mb: 3 }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={420}
        sx={{ borderRadius: "24px" }}
      />
    </Container>
  </Box>
);

export default function ClassAnalysisPage() {
  const { id } = useParams();
  const query = useQuery();
  const navigate = useNavigate();
  const term = query.get("term") || "";
  const session = query.get("session") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchClassAnalysis(id, term, session)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [id, term, session]);

  if (loading) return <PageSkeleton />;
  if (error)
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: "20px" }}
          action={
            <Button onClick={() => navigate(-1)} size="small">
              Go Back
            </Button>
          }
        >
          Failed to load class analysis.
        </Alert>
      </Container>
    );
  if (!data) return <PageSkeleton />;

  const gradeData = Object.entries(data.grade_distribution || {}).map(
    ([grade, count]) => ({ grade, count })
  );

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <AmbientBackground />
      <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
        {/* back */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: "#4b5563",
              fontWeight: 700,
              px: 2,
              background: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "12px",
              backdropFilter: "blur(12px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              "&:hover": { background: "rgba(255,255,255,0.95)" },
            }}
          >
            Back to Dashboard
          </Button>
        </motion.div>

        {/* header */}
        <MotionBox
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "18px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(139,92,246,0.4)",
              }}
            >
              <ClassRoundedIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 800,
                  color: "#111827",
                  letterSpacing: "-0.04em",
                }}
              >
                {data.class}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#9ca3af", fontSize: "0.8rem" }}
              >
                Class performance analysis
              </Typography>
            </Box>
          </Stack>
        </MotionBox>

        {/* stats */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <StatBadge
            icon={<TrendingUpRoundedIcon />}
            label="Class Average"
            value={`${parseFloat(data.class_average || 0).toFixed(1)}%`}
            color="#6366f1"
            gradient="linear-gradient(135deg, #6366f1, #818cf8)"
            index={0}
          />
          <StatBadge
            icon={<CheckCircleOutlineRoundedIcon />}
            label="Pass Rate"
            value={`${parseFloat(data.pass_rate || 0).toFixed(1)}%`}
            color="#10b981"
            gradient="linear-gradient(135deg, #10b981, #34d399)"
            index={1}
          />
          <StatBadge
            icon={<GroupRoundedIcon />}
            label="Total Students"
            value={data.total_students || 0}
            color="#f59e0b"
            gradient="linear-gradient(135deg, #f59e0b, #fbbf24)"
            index={2}
          />
        </Box>

        {/* subject insights */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #10b981, #6366f1)",
            }}
          />
          <CardContent sx={{ p: "20px !important" }}>
            <Typography
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontSize: "0.66rem",
                fontWeight: 700,
                color: "#9ca3af",
                mb: 2,
                letterSpacing: "0.1em",
              }}
            >
              SUBJECT INSIGHTS
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {data.strongest_subject && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: "14px",
                    background: "rgba(16,185,129,0.07)",
                    border: "1px solid rgba(16,185,129,0.18)",
                  }}
                >
                  <StarRoundedIcon sx={{ color: "#10b981", fontSize: 22 }} />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.66rem",
                        color: "#10b981",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                      }}
                    >
                      STRONGEST
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {data.strongest_subject?.subject__name}
                    </Typography>
                  </Box>
                </Stack>
              )}
              {data.weakest_subject && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: "14px",
                    background: "rgba(239,68,68,0.06)",
                    border: "1px solid rgba(239,68,68,0.18)",
                  }}
                >
                  <WarningAmberRoundedIcon
                    sx={{ color: "#ef4444", fontSize: 22 }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.66rem",
                        color: "#ef4444",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                      }}
                    >
                      WEAKEST
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {data.weakest_subject?.subject__name}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </MotionCard>

        {/* grade distribution */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                color: "#111827",
                mb: 0.5,
              }}
            >
              Grade Distribution
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", fontSize: "0.78rem", mb: 3 }}
            >
              Number of students per grade bracket
            </Typography>
            {gradeData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.disabled">
                  No grade distribution data
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={gradeData}
                  margin={{ top: 20, right: 8, left: -16, bottom: 0 }}
                  barSize={38}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.05)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="grade"
                    tick={{ fontSize: 12, fontWeight: 700, fill: "#4b5563" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(99,102,241,0.05)", rx: 6 }}
                  />
                  <Bar dataKey="count" radius={[10, 10, 0, 0]}>
                    {gradeData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={getGradeColor(entry.grade)}
                        fillOpacity={0.88}
                      />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fontWeight: 700, fill: "#4b5563" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </MotionCard>

        {/* leaderboard */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          sx={{ borderRadius: "24px", overflow: "hidden", ...glassMixin }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
              <EmojiEventsRoundedIcon sx={{ color: "#f59e0b", fontSize: 22 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Class Leaderboard
              </Typography>
              <Chip
                label={`${data.leaderboard?.length || 0} students`}
                size="small"
                sx={{
                  background: "rgba(245,158,11,0.1)",
                  color: "#f59e0b",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  border: "1px solid rgba(245,158,11,0.22)",
                  borderRadius: "8px",
                }}
              />
            </Stack>

            {!data.leaderboard?.length ? (
              <Typography
                color="text.disabled"
                sx={{ textAlign: "center", py: 3 }}
              >
                No leaderboard data available
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {data.leaderboard.map((s, i) => {
                  const score = parseFloat(s.avg || 0).toFixed(1);
                  const rankMedal =
                    i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                  const rowBg =
                    i === 0
                      ? "rgba(245,158,11,0.06)"
                      : i === 1
                      ? "rgba(100,116,139,0.05)"
                      : i === 2
                      ? "rgba(160,82,45,0.05)"
                      : "transparent";
                  const rowBorder =
                    i < 3
                      ? `1px solid ${
                          [
                            "rgba(245,158,11,0.2)",
                            "rgba(100,116,139,0.18)",
                            "rgba(160,82,45,0.18)",
                          ][i]
                        }`
                      : "1px solid transparent";
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, delay: 0.4 + i * 0.05 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{
                          p: "12px 16px",
                          borderRadius: "14px",
                          background: rowBg,
                          border: rowBorder,
                          "&:hover": {
                            background: i < 3 ? rowBg : "rgba(99,102,241,0.04)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            width: 28,
                            textAlign: "center",
                            fontWeight: 800,
                            color: "#9ca3af",
                            fontSize: i < 3 ? "1.3rem" : "0.82rem",
                            fontFamily: "'Syne', sans-serif",
                          }}
                        >
                          {rankMedal || `#${i + 1}`}
                        </Typography>
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            color: "#fff",
                          }}
                        >
                          {(s.inststudent__name || "?")
                            .split(" ")
                            .slice(0, 2)
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()}
                        </Avatar>
                        <Typography
                          sx={{
                            flex: 1,
                            fontWeight: 700,
                            color: "#111827",
                            fontSize: "0.875rem",
                            fontFamily: "'Syne', sans-serif",
                          }}
                        >
                          {s.inststudent__name}
                        </Typography>
                        <Box sx={{ width: 110 }}>
                          <Box
                            sx={{
                              height: 4,
                              background: "rgba(0,0,0,0.06)",
                              borderRadius: "100px",
                              overflow: "hidden",
                            }}
                          >
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${parseFloat(score)}%` }}
                              transition={{
                                duration: 1.2,
                                delay: 0.5 + i * 0.05,
                              }}
                              style={{
                                height: "100%",
                                background:
                                  parseFloat(score) >= 70
                                    ? "linear-gradient(90deg, #10b981, #34d399)"
                                    : parseFloat(score) >= 50
                                    ? "linear-gradient(90deg, #6366f1, #818cf8)"
                                    : "linear-gradient(90deg, #ef4444, #f87171)",
                                borderRadius: "100px",
                              }}
                            />
                          </Box>
                        </Box>
                        <Chip
                          label={`${score}%`}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.76rem",
                            background: "rgba(99,102,241,0.09)",
                            color: "#6366f1",
                            border: "1px solid rgba(99,102,241,0.2)",
                            borderRadius: "8px",
                          }}
                        />
                      </Stack>
                    </motion.div>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </MotionCard>
      </Container>
    </Box>
  );
}
